import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../../auth/useAuth.js';
import { usePermissions } from '../../auth/usePermissions.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hasPlatformSession } from '../../platform/platformSession.js';
import { setForbiddenHandler } from '../../services/apiClient.js';
import { getProfile } from '../../services/authApi.js';
import { initials } from '../../utils/formatters.js';
import { showToast, Toaster } from '../ui/Toaster.jsx';

import { CommandPalette, openCommandPalette } from './CommandPalette.jsx';
import { ErrorBoundary } from './ErrorBoundary.jsx';
import { NotificationBell } from './NotificationBell.jsx';
import { ThemeToggle } from './ThemeToggle.jsx';

const NAV_SECTIONS = [
  {
    labelKey: 'nav.section.overview',
    items: [
      { to: '/', labelKey: 'nav.dashboard', end: true },
      { to: '/inbox', labelKey: 'nav.inbox' },
      { to: '/calendar', labelKey: 'nav.calendar' },
    ],
  },
  {
    /* Steht bewusst weit oben und über den Fachbereichen: für die meisten
       im Haus ist das der einzige Punkt, den sie brauchen. */
    labelKey: 'nav.section.self',
    module: 'self',
    items: [
      /* Drei Seiten heissen nach 'Zeit'. Wer in der Suche danach tippt,
         bekommt alle drei - der Halbsatz sagt, welche gemeint ist. */
      { to: '/meine/zeit', labelKey: 'nav.myTime', hintKey: 'nav.myTime.hint' },
      { to: '/meine/ferien', labelKey: 'nav.myLeave' },
      /* Zu meiner Entscheidung. Steht auch bei denen im Menü, die niemanden
         führen - die Seite sagt dann in einem Satz, warum sie leer ist. Das
         ist ehrlicher als ein Punkt, der je nach Person verschwindet und
         niemandem erklärt, wohin er verschwunden ist. */
      { to: '/meine/team', labelKey: 'nav.myTeam' },
    ],
  },
  {
    labelKey: 'nav.section.finance',
    module: 'finance',
    items: [
      { to: '/finance/overview', labelKey: 'nav.financeDashboard' },
      { to: '/finance/accounts', labelKey: 'nav.accounts' },
      { to: '/finance/invoices', labelKey: 'nav.invoices' },
      { to: '/finance/dunning',  labelKey: 'nav.dunning' },
      { to: '/finance/credit-notes', labelKey: 'nav.creditNotes' },
      { to: '/finance/recurring', labelKey: 'nav.recurring' },
      { to: '/finance/cost-centers', labelKey: 'nav.costCenters' },
      { to: '/finance/controlling', labelKey: 'nav.controlling' },
      { to: '/finance/profit-centers', labelKey: 'nav.profitCenters' },
      { to: '/finance/down-payments', labelKey: 'nav.downPayments' },
      { to: '/finance/intercompany', labelKey: 'nav.intercompany' },
      { to: '/finance/budgets', labelKey: 'nav.budgets' },
      { to: '/finance/journal', labelKey: 'nav.journal' },
      { to: '/finance/cash-journal', labelKey: 'nav.cashJournal' },
      { to: '/finance/recurring-journals', labelKey: 'nav.recurringJournals' },
      { to: '/finance/payment-terms', labelKey: 'nav.paymentTerms' },
      { to: '/finance/collective-invoices', labelKey: 'nav.collectiveInvoice' },
      { to: '/finance/report-builder', labelKey: 'nav.reportBuilder' },
      { to: '/finance/fixed-assets', labelKey: 'nav.fixedAssets' },
      { to: '/finance/maintenance', labelKey: 'nav.maintenance' },
      { to: '/finance/period-close', labelKey: 'nav.periodClose' },
      { to: '/finance/bank',     labelKey: 'nav.bank' },
      { to: '/finance/payment-runs', labelKey: 'nav.paymentRuns' },
      { to: '/finance/reports',  labelKey: 'nav.reports' },
      { to: '/finance/vat',      labelKey: 'nav.vat' },
    ],
  },
  {
    labelKey: 'nav.section.hr',
    module: 'hr',
    items: [
      { to: '/hr/overview', labelKey: 'nav.hrDashboard' },
      { to: '/hr/employees', labelKey: 'nav.employees' },
      { to: '/hr/leave',     labelKey: 'nav.leave' },
      { to: '/hr/leave-types', labelKey: 'nav.leaveTypes' },
      { to: '/hr/payroll',   labelKey: 'nav.payroll' },
      { to: '/hr/withholding-tax', labelKey: 'nav.withholdingTax' },
      { to: '/hr/time',      labelKey: 'nav.time', hintKey: 'nav.time.hint' },
      { to: '/hr/travel',    labelKey: 'nav.travel' },
      { to: '/hr/reviews',   labelKey: 'nav.reviews' },
    ],
  },
  {
    labelKey: 'nav.section.recruitment',
    module: 'hr',
    items: [
      { to: '/hr/job-postings', labelKey: 'nav.jobPostings' },
      { to: '/hr/candidates',   labelKey: 'nav.candidates' },
      { to: '/hr/applications', labelKey: 'nav.applications' },
    ],
  },
  {
    labelKey: 'nav.section.inventory',
    module: 'inventory',
    items: [
      { to: '/inventory/overview',         labelKey: 'nav.inventoryDashboard' },
      { to: '/inventory/products',         labelKey: 'nav.products' },
      { to: '/inventory/low-stock',        labelKey: 'nav.lowStock' },
      { to: '/inventory/suppliers',        labelKey: 'nav.suppliers' },
      { to: '/inventory/stock',            labelKey: 'nav.stockMovements' },
      { to: '/inventory/lots',             labelKey: 'nav.lots' },
      { to: '/inventory/bins',             labelKey: 'nav.bins' },
      { to: '/inventory/quality',          labelKey: 'nav.quality' },
      { to: '/inventory/consignment',      labelKey: 'nav.consignment' },
      { to: '/inventory/contracts',        labelKey: 'nav.purchaseContracts' },
      { to: '/inventory/scheduling-agreements', labelKey: 'nav.schedulingAgreements' },
      { to: '/inventory/serials',           labelKey: 'nav.serials' },
      { to: '/inventory/vendor-evaluation', labelKey: 'nav.vendorEvaluation' },
      { to: '/inventory/units',             labelKey: 'nav.uom' },
      { to: '/inventory/backorders',        labelKey: 'nav.backorders' },
      { to: '/inventory/mrp-run',           labelKey: 'nav.mrpRun' },
      { to: '/inventory/atp',              labelKey: 'nav.atp' },
      { to: '/inventory/cycle-counts',     labelKey: 'nav.cycleCounts' },
      { to: '/inventory/requisitions',     labelKey: 'nav.requisitions' },
      { to: '/inventory/reservations',     labelKey: 'nav.reservations' },
      { to: '/inventory/purchase-orders',  labelKey: 'nav.purchaseOrders' },
    ],
  },
  {
    labelKey: 'nav.section.sales',
    module: 'sales',
    items: [
      { to: '/sales/overview', labelKey: 'nav.salesDashboard' },
      { to: '/sales/customers', labelKey: 'nav.customers' },
      { to: '/sales/price-lists', labelKey: 'nav.priceLists' },
      { to: '/sales/quotes',    labelKey: 'nav.quotes' },
      { to: '/sales/orders',    labelKey: 'nav.orders' },
      { to: '/sales/credit',    labelKey: 'nav.creditManagement' },
      { to: '/sales/deliveries', labelKey: 'nav.deliveries' },
      { to: '/sales/returns',   labelKey: 'nav.returns' },
      { to: '/sales/contracts', labelKey: 'nav.contracts' },
      { to: '/sales/tickets',   labelKey: 'nav.tickets' },
      { to: '/sales/pipeline',  labelKey: 'nav.pipeline' },
      { to: '/sales/reports',   labelKey: 'nav.reports' },
    ],
  },
  {
    labelKey: 'nav.section.projects',
    module: 'projects',
    items: [
      { to: '/projects/overview', labelKey: 'nav.projectsDashboard' },
      { to: '/projects/list', labelKey: 'nav.projectsList' },
      { to: '/projects/time', labelKey: 'nav.projectsTime', hintKey: 'nav.projectsTime.hint' },
      { to: '/projects/expenses', labelKey: 'nav.projectsExpenses' },
    ],
  },
  {
    labelKey: 'nav.section.production',
    module: 'production',
    items: [
      { to: '/production/overview', labelKey: 'nav.productionDashboard' },
      { to: '/production/boms', labelKey: 'nav.boms' },
      { to: '/production/work-centers', labelKey: 'nav.workCenters' },
      { to: '/production/work-orders', labelKey: 'nav.workOrders' },
      { to: '/production/mrp', labelKey: 'nav.mrp' },
      { to: '/production/capacity', labelKey: 'nav.capacity' },
    ],
  },
  {
    labelKey: 'nav.section.automation',
    module: 'admin',
    items: [
      { to: '/automation/rules', labelKey: 'nav.rules' },
      { to: '/automation/alerts', labelKey: 'nav.alerts' },
    ],
  },
  {
    labelKey: 'nav.section.account',
    items: [{ to: '/settings', labelKey: 'nav.settings' }],
  },
];

function sectionContainsActive(items, pathname) {
  return items.some((item) =>
    item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`),
  );
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <line x1="16.5" y1="16.5" x2="21" y2="21" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      className="nav-group-chevron"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

function CaretIcon() {
  return (
    <svg
      className="menubar-caret"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/* Desktop top menu bar: each multi-item section is a click-to-open dropdown;
   single-item sections (Dashboard, Settings) are plain links. */
function MenuBar({ sections, t }) {
  const location = useLocation();
  const [openKey, setOpenKey] = useState(null);
  const barRef = useRef(null);

  // Close on navigation.
  useEffect(() => {
    setOpenKey(null);
  }, [location.pathname]);

  // Close on click-outside / Escape while a dropdown is open.
  useEffect(() => {
    if (!openKey) {
      return undefined;
    }
    function handlePointer(event) {
      if (barRef.current && !barRef.current.contains(event.target)) {
        setOpenKey(null);
      }
    }
    function handleKey(event) {
      if (event.key === 'Escape') {
        setOpenKey(null);
      }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [openKey]);

  return (
    <nav className="menubar" ref={barRef} aria-label={t('aria.hauptnavigation')}>
      {sections.map((section) => {
        if (section.items.length === 1) {
          const only = section.items[0];
          return (
            <NavLink
              key={section.labelKey}
              to={only.to}
              end={only.end}
              className={({ isActive }) => `menubar-item${isActive ? ' active' : ''}`}
            >
              <span className="menubar-label">{t(only.labelKey ?? section.labelKey)}</span>
            </NavLink>
          );
        }

        const isOpen = openKey === section.labelKey;
        const hasActive = sectionContainsActive(section.items, location.pathname);

        return (
          <div className="menubar-group" key={section.labelKey}>
            <button
              type="button"
              className={`menubar-item${hasActive ? ' active' : ''}${isOpen ? ' open' : ''}`}
              aria-haspopup="true"
              aria-expanded={isOpen}
              onClick={() => setOpenKey(isOpen ? null : section.labelKey)}
            >
              <span className="menubar-label">{t(section.labelKey)}</span>
              <CaretIcon />
            </button>
            {isOpen && (
              <div className="menu-dropdown" role="menu">
                {section.items.map(({ to, labelKey, end }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    role="menuitem"
                    className={({ isActive }) => `menu-dropdown-item${isActive ? ' active' : ''}`}
                    onClick={() => setOpenKey(null)}
                  >
                    {t(labelKey)}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}

/* Collapsible module group used inside the mobile drawer. */
function NavGroup({ section, t }) {
  const location = useLocation();
  const items = section.items;
  const isSingle = items.length === 1;

  const containsActive = useMemo(
    () => sectionContainsActive(items, location.pathname),
    [items, location.pathname],
  );

  const [open, setOpen] = useState(containsActive);

  useEffect(() => {
    if (containsActive) {
      setOpen(true);
    }
  }, [containsActive]);

  if (isSingle) {
    const only = items[0];
    return (
      <NavLink
        to={only.to}
        end={only.end}
        className={({ isActive }) => (isActive ? 'nav-link nav-link-top active' : 'nav-link nav-link-top')}
      >
        {t(only.labelKey ?? section.labelKey)}
      </NavLink>
    );
  }

  return (
    <div className={`nav-group${open ? ' open' : ''}`}>
      <button
        type="button"
        className={`nav-group-header${containsActive ? ' has-active' : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span>{t(section.labelKey)}</span>
        <ChevronIcon />
      </button>
      {open && (
        <div className="nav-group-items">
          {items.map(({ to, labelKey, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
            >
              {t(labelKey)}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu() {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  // Avatar photo from "Mein Profil" - falls back to initials while empty.
  const profile = useQuery({ queryKey: ['auth', 'profile'], queryFn: getProfile, retry: false });
  const photo = profile.data?.photo || null;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    function handlePointer(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKey(event) {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointer);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handlePointer);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  return (
    <div className="user-menu" ref={menuRef}>
      <button
        type="button"
        className="avatar avatar-button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={fullName || user?.email}
        title={fullName || user?.email}
        onClick={() => setOpen((value) => !value)}
      >
        {photo
          ? <img src={photo} alt={fullName || user?.email} className="avatar-photo" />
          : initials(user?.firstName || user?.email?.charAt(0), user?.lastName || '')}
      </button>
      {open && (
        <div className="user-menu-panel" role="menu">
          <div className="user-menu-identity">
            {fullName && <span className="user-menu-name">{fullName}</span>}
            <span className="user-menu-email">{user?.email}</span>
          </div>
          <button
            type="button"
            className="user-menu-item"
            role="menuitem"
            onClick={() => { setOpen(false); navigate('/profile'); }}
          >
            {t('profile.menu')}
          </button>
          {/* Einstieg in die Anbietersicht, nur sichtbar wenn eine
              Anbietersitzung im Browser liegt. In einer Kundeninstallation gibt
              es die nie, dort erfährt niemand von dieser Ebene. Es ist ein
              reiner Sichtbarkeits-Hinweis; der Zugang hängt an der Signatur
              des Anbietertokens, nicht an diesem Eintrag. */}
          {hasPlatformSession() && (
            <a className="user-menu-item" role="menuitem" href="/platform/tenants">
              {t('platform.badge')}
            </a>
          )}
          <button type="button" className="user-menu-item" role="menuitem" onClick={signOut}>
            {t('common.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { t } = useTranslation();
  const { canRead } = usePermissions();
  const visibleSections = NAV_SECTIONS.filter((s) => !s.module || canRead(s.module));
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setDrawerOpen((prev) => !prev), []);

  // Surface 403 responses globally: server message if present, i18n fallback otherwise.
  useEffect(() => {
    setForbiddenHandler((serverMessage) => showToast(serverMessage || t('access.denied')));
    return () => setForbiddenHandler(null);
  }, [t]);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (!isDrawerOpen) {
      return undefined;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isDrawerOpen]);

  // Esc closes the drawer.
  useEffect(() => {
    if (!isDrawerOpen) {
      return undefined;
    }
    function handleKey(event) {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isDrawerOpen, closeDrawer]);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-left">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleDrawer}
            aria-label={isDrawerOpen ? t('aria.menueSchliessen') : t('aria.menueOeffnen')}
            aria-expanded={isDrawerOpen}
          >
            <HamburgerIcon />
          </button>
          <div className="brand">
            <span className="brand-mark" aria-hidden="true" />
            <span>RSZ ERP</span>
          </div>
        </div>

        <MenuBar sections={visibleSections} t={t} />

        <button
          type="button"
          className="palette-trigger"
          onClick={openCommandPalette}
          aria-label={t('palette.open')}
          title={t('palette.open')}
        >
          <SearchIcon />
          <span className="palette-trigger-keys" aria-hidden="true">
            <kbd>Ctrl</kbd>
            <kbd>K</kbd>
            <span className="palette-trigger-or">·</span>
            <kbd>/</kbd>
          </span>
        </button>

        <div className="topbar-user">
          <ThemeToggle />
          <NotificationBell />
          <UserMenu />
        </div>
      </header>

      {/* Mobile drawer (hamburger) */}
      <div
        className={`sidebar-backdrop${isDrawerOpen ? ' open' : ''}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />
      <aside className={`sidebar${isDrawerOpen ? ' open' : ''}`} aria-label={t('aria.hauptnavigation')}>
        <nav>
          {visibleSections.map((section) => (
            <NavGroup key={section.labelKey} section={section} t={t} />
          ))}
        </nav>
      </aside>

      {/* Die Fehlergrenze steht INNERHALB von main, nicht darum herum: so
          bleiben Kopfzeile und Navigation stehen, wenn eine Seite zerbricht,
          und man klickt einfach woandershin. Der Pfad als Schlüssel räumt die
          Tafel beim Seitenwechsel von selbst weg. */}
      <main className="content">
        <ErrorBoundary resetKey={location.pathname} onHome={() => navigate('/')}>
          <Outlet />
        </ErrorBoundary>
      </main>

      <CommandPalette sections={visibleSections} t={t} />

      <Toaster />
    </div>
  );
}
